import type { CSSProperties, ReactElement, ReactNode } from "react";
import { palette, space } from "@ux/tokens";
import { useViewport } from "@ux/hooks";

export interface ToolbarProps {
    readonly leading?: ReactNode;
    readonly trailing?: ReactNode;
    readonly bordered?: boolean;
    readonly style?: CSSProperties;
}

/**
 * Page-level toolbar with a leading slot (typically a heading + status
 * badges) and a trailing slot (search inputs, filters, primary CTA).
 *
 * Below the desktop breakpoint, the inner row gains `flex-wrap: wrap`
 * so trailing children that don't fit (the 220-px search input, the
 * 200-px office filter, etc.) drop to a new line instead of being
 * clipped. Pages should typically pair this with moving the primary CTA
 * out of the trailing slot and into a `<BottomActionBar>` for thumb-zone
 * placement on phone.
 */
export function Toolbar({
    leading,
    trailing,
    bordered = true,
    style,
}: ToolbarProps): ReactElement {
    const { isDesktop } = useViewport();

    const composed: CSSProperties = {
        display: "flex",
        alignItems: isDesktop ? "center" : "flex-start",
        justifyContent: "space-between",
        gap: space[3],
        padding: `${space[3]} ${space[4]}`,
        borderBottom: bordered ? `1px solid ${palette.border}` : undefined,
        // Allow the leading/trailing groups themselves to wrap onto
        // separate rows below desktop. The inner groups handle their
        // own children's wrapping.
        flexWrap: isDesktop ? "nowrap" : "wrap",
        ...style,
    };

    const leadingStyle: CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: space[2],
        flex: 1,
        minWidth: 0,
    };

    const trailingStyle: CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: space[2],
        // Wrap trailing children below desktop so e.g. search + filter +
        // toggle stack vertically when the screen can't host them all in
        // a row. On desktop the existing single-row behaviour is preserved.
        flexWrap: isDesktop ? "nowrap" : "wrap",
    };

    return (
        <div style={composed}>
            <div style={leadingStyle}>{leading}</div>
            <div style={trailingStyle}>{trailing}</div>
        </div>
    );
}
